import { useCallback } from "react";
import type { CallBackProps } from "react-joyride";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";

import { TOUR_FINISHED_STATUSES } from "@/constants/tour";
import type { IUseTourJoyrideCallbackProps } from "@/providers/types";
import { getStepDirection } from "@/providers/utils";

export const useTourJoyrideCallback = ({
  steps,
  handleStepNavigation,
  resetTour,
  onTargetNotFound,
}: IUseTourJoyrideCallbackProps): ((data: CallBackProps) => void) => {
  return useCallback(
    (data: CallBackProps): void => {
      const { status, action, index, type } = data;

      // NOTE: After page navigation the target element may not be rendered yet.
      // Schedule a delayed retry so Joyride re-attempts once the DOM is ready.
      if (type === EVENTS.TARGET_NOT_FOUND) {
        onTargetNotFound?.(index);
        return;
      }

      // NOTE: In controlled mode (stepIndex), joyride does not auto-set
      // status to FINISHED when the last step's Done button is clicked.
      // Detect this explicitly so the tour can complete.
      const isLastStepDone =
        type === EVENTS.STEP_AFTER &&
        action === ACTIONS.NEXT &&
        index >= steps.length - 1;

      if (type === EVENTS.STEP_AFTER && !isLastStepDone) {
        const direction = getStepDirection(action);

        if (direction !== undefined) {
          const nextIndex = index + direction;

          if (nextIndex >= 0 && nextIndex < steps.length) {
            handleStepNavigation({ nextIndex, currentIndex: index });
          }
        }
      }

      const isTourEnding =
        TOUR_FINISHED_STATUSES.includes(status) ||
        action === ACTIONS.CLOSE ||
        isLastStepDone;

      if (isTourEnding) {
        // NOTE: NSAI-4615 — "completed" means the user reached the end: either
        // Joyride reported FINISHED, or they pressed Done on the last step in
        // controlled mode (which Joyride does not mark FINISHED itself).
        // Everything else here — SKIPPED, or the close button — is an
        // abandonment, and conflating the two would make the tour look
        // perfect while nobody actually finished it.
        resetTour({
          isCompleted: isLastStepDone || status === STATUS.FINISHED,
        });
      }
    },
    [steps, handleStepNavigation, resetTour, onTargetNotFound],
  );
};
